"use client";

import type { ChatStatus, FileUIPart } from "ai";
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { AttachFilesButton } from "@/components/ai-elements/attach-files-button";
import { AttachmentPreview } from "@/components/ai-elements/attachment-preview";
import {
  PromptInput,
  PromptInputSubmit,
  PromptInputTextarea,
  useOptionalPromptInputController,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
  VoiceNoteButton,
  type VoiceNoteButtonHandle,
} from "@/components/ai-elements/voice-note-button";
import { Spinner } from "@/components/ui/spinner";
import { useIsKeyboardOpen } from "@/hooks/use-keyboard-open";
import { useIsMobile } from "@/hooks/use-media-query";
import {
  dispatchVoiceDictationShortcut,
  useVoiceDictationShortcut,
  type VoiceDictationShortcutTarget,
} from "@/hooks/use-voice-dictation-shortcut";
import { cn } from "@/lib/utils";

/** Shared client-side upload limits mirrored from UploadService defaults. */
export const CHAT_MAX_UPLOAD_FILES = 10;
export const CHAT_MAX_UPLOAD_SIZE_MB = 2048;
export const CHAT_MAX_UPLOAD_SIZE_BYTES = CHAT_MAX_UPLOAD_SIZE_MB * 1024 * 1024;
const COMPOSER_STACK_ENTER_WIDTH = 400;
const COMPOSER_STACK_EXIT_WIDTH = 440;
// Five 32px inline controls, four 4px gaps, the 8px row gap, and 26px of
// textarea horizontal padding/borders. Always estimate the unstacked layout.
const COMPOSER_INLINE_RESERVED_WIDTH = 5 * 32 + 4 * 4 + 8 + 26;
const COMPOSER_DESKTOP_FONT_SIZE = 14; // Textarea's md:text-sm.
const COMPOSER_AVERAGE_CHAR_WIDTH = 0.55;

function estimatedDraftLines(containerWidth: number, fontSize: number, draftLength: number) {
  const textWidth = Math.max(0, containerWidth - COMPOSER_INLINE_RESERVED_WIDTH);
  const charsPerLine = Math.max(1, Math.floor(textWidth / (fontSize * COMPOSER_AVERAGE_CHAR_WIDTH)));
  return Math.ceil(draftLength / charsPerLine);
}

export interface ChatPromptComposerProps {
  /** Changes whenever this composer should reset its local layout state. */
  inputKey: string;
  placeholder: string;
  inputAriaLabel?: string;
  onSubmit: (message: PromptInputMessage) => void | Promise<void>;
  onInputChange?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onMultilineChange?: (multiline: boolean) => void;
  onVoiceDictationShortcut?: () => void;
  onVoiceNoteSubmit: (text: string, file: File, existingFiles: FileUIPart[]) => void;
  onVoiceSubmit?: () => void;
  onRecordingChange?: (recording: boolean) => void;
  voiceNoteRef?: RefObject<VoiceNoteButtonHandle>;
  submitButtonRef?: RefObject<HTMLButtonElement>;
  rowRef?: RefObject<HTMLDivElement>;
  autoFocusKey?: string;
  textareaName?: string;
  promptQueue?: ReactNode;
  enterToSendHint?: ReactNode;
  /** Optional render callback shares the composer's action stacking state. */
  quickActions?: ReactNode | ((layout: { stacked: boolean }) => ReactNode);
  followUpSuggestions?: ReactNode;
  selectedSkillBadges?: ReactNode;
  uploadProgress?: ReactNode;
  transcriptionProgress?: ReactNode;
  stopControl?: ReactNode;
  submitDisabled?: boolean;
  status?: ChatStatus;
  isUploading?: boolean;
  isTranscribing?: boolean;
  onStop?: () => void;
  disabled?: boolean;
  /** Hide the regular text-submit control while retaining keyboard submit. */
  hideSubmit?: boolean;
  /** Optional root classes for context-specific layout adjustments. */
  className?: string;
  /** Optional textarea classes for context-specific layout adjustments. */
  textareaClassName?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  onError?: (error: {
    code: "max_files" | "max_file_size" | "accept";
    message: string;
  }) => void;
  /** Effective server-backed desktop send shortcut. */
  submitShortcut?: "enter" | "ctrl_enter";
  /** Scope that receives Cmd/Ctrl+/ voice-dictation requests. */
  voiceDictationShortcutTarget?: VoiceDictationShortcutTarget;
}

/**
 * The canonical chat composer used by both ordinary sessions and a project
 * task before its first session is bound. Keeping the implementation here
 * prevents project screens from drifting from the normal attachment, voice,
 * preview, and responsive action layout.
 *
 * A <PromptInputProvider> must wrap this component. Normal ChatInterface and
 * the project task starter each provide one so files, voice-note text, and
 * submit state follow the same path in both places.
 */
export function ChatPromptComposer({
  inputKey,
  placeholder,
  inputAriaLabel,
  onSubmit,
  onInputChange,
  onKeyDown,
  onMultilineChange,
  onVoiceDictationShortcut,
  onVoiceNoteSubmit,
  onVoiceSubmit,
  onRecordingChange,
  voiceNoteRef: externalVoiceNoteRef,
  submitButtonRef,
  rowRef,
  autoFocusKey,
  textareaName = "message",
  promptQueue,
  enterToSendHint,
  quickActions,
  followUpSuggestions,
  selectedSkillBadges,
  uploadProgress,
  transcriptionProgress,
  stopControl,
  submitDisabled,
  status = "ready",
  isUploading = false,
  isTranscribing = false,
  onStop,
  disabled = false,
  hideSubmit = false,
  className,
  textareaClassName,
  multiple = false,
  maxFiles,
  maxFileSize,
  onError,
  submitShortcut = "enter",
  voiceDictationShortcutTarget,
}: ChatPromptComposerProps) {
  const { t } = useTranslation();
  const promptController = useOptionalPromptInputController();
  const isMobile = useIsMobile();
  const isKeyboardOpen = useIsKeyboardOpen();
  const [promptMultiline, setPromptMultiline] = useState(false);
  const [containerNarrow, setContainerNarrow] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [draftLength, setDraftLength] = useState(() => promptController?.textInput.value.length ?? 0);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const internalRowRef = useRef<HTMLDivElement | null>(null);
  const promptInputValueRef = useRef("");
  const internalVoiceNoteRef = useRef<VoiceNoteButtonHandle>(null);
  const internalSubmitRef = useRef<HTMLButtonElement>(null);
  const voiceNoteRef = externalVoiceNoteRef ?? internalVoiceNoteRef;
  const voiceSubmitRef = submitButtonRef ?? internalSubmitRef;
  useImperativeHandle(rowRef, () => internalRowRef.current!, [rowRef]);

  useEffect(() => {
    const row = internalRowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const width = entry.contentRect.width;
      setContainerWidth(width);
      // Hysteresis keeps small width changes around the breakpoint from
      // toggling the action layout repeatedly.
      setContainerNarrow((narrow) => narrow
        ? width < COMPOSER_STACK_EXIT_WIDTH
        : width < COMPOSER_STACK_ENTER_WIDTH);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  // Provider drafts may be restored or cleared without a textarea change event.
  useEffect(() => {
    if (!promptController) return;
    promptInputValueRef.current = promptController.textInput.value;
    setDraftLength(promptController.textInput.value.length);
  }, [promptController?.textInput.value]);

  const startVoiceDictation = useCallback(() => {
    voiceNoteRef.current?.startRecording();
  }, []);
  const handleVoiceDictationShortcut = useCallback(() => {
    if (voiceDictationShortcutTarget) {
      dispatchVoiceDictationShortcut(voiceDictationShortcutTarget);
    }
  }, [voiceDictationShortcutTarget]);

  useVoiceDictationShortcut(voiceDictationShortcutTarget, startVoiceDictation);

  useEffect(() => {
    if (!isRecordingVoice) return;
    // Run after the textarea's initial auto-focus frame, which otherwise can
    // reclaim focus when recording begins immediately after mounting.
    const focusVoiceSubmit = () => voiceSubmitRef.current?.focus();
    if (typeof requestAnimationFrame === "undefined") {
      focusVoiceSubmit();
      return;
    }
    const frame = requestAnimationFrame(focusVoiceSubmit);
    return () => cancelAnimationFrame(frame);
  }, [isRecordingVoice]);

  const handleMultilineChange = useCallback((multiline: boolean) => {
    onMultilineChange?.(multiline);
    if (multiline) {
      setPromptMultiline(true);
      return;
    }
    // Only un-stack when the input is actually empty. This avoids the mobile
    // width/line-wrap feedback loop the normal session composer already guards.
    setPromptMultiline(promptInputValueRef.current.trim() !== "");
  }, [onMultilineChange]);

  const resetLayout = useCallback(() => {
    promptInputValueRef.current = "";
    setDraftLength(0);
    setPromptMultiline(false);
  }, []);

  useEffect(() => {
    resetLayout();
  }, [inputKey, resetLayout]);

  // During an active run, the parent submit handler uses the durable prompt-queue
  // (session_wake_events), not the bridge's ephemeral Chat SDK message queue.
  const handleSubmit = useCallback(async (message: PromptInputMessage) => {
    resetLayout();
    await onSubmit(message);
  }, [onSubmit, resetLayout]);

  const handleVoiceNoteSubmit = useCallback((
    text: string,
    file: File,
    existingFiles: FileUIPart[],
  ) => {
    resetLayout();
    onVoiceNoteSubmit(text, file, existingFiles);
  }, [onVoiceNoteSubmit, resetLayout]);

  const promptActionsVertical = (isMobile && promptMultiline) ||
    (!isMobile && containerNarrow &&
      estimatedDraftLines(containerWidth, COMPOSER_DESKTOP_FONT_SIZE, draftLength) > 1);
  const controlsDisabled =
    disabled ||
    status === "submitted" ||
    status === "streaming" ||
    isUploading ||
    isTranscribing;

  return (
    <PromptInput
      onSubmit={handleSubmit}
      className={cn("shrink-0 border-t bg-background p-4 flex flex-col gap-2", className)}
      multiple={multiple}
      maxFiles={maxFiles}
      maxFileSize={maxFileSize}
      onError={onError}
    >
      {promptQueue}
      <AttachmentPreview />
      {isUploading && (uploadProgress ?? (
        <div
          className="flex items-center gap-2 px-1 pb-1 text-xs text-muted-foreground"
          data-testid="upload-progress-indicator"
        >
          <Spinner className="size-3.5" />
          <span>{t("common:status.uploadingFiles")}</span>
        </div>
      ))}
      {isTranscribing && (transcriptionProgress ?? (
        <div
          className="flex items-center gap-2 px-1 pb-1 text-xs text-muted-foreground"
          data-testid="transcribe-progress-indicator"
        >
          <Spinner className="size-3.5" />
          <span>{t("common:status.transcribingVoiceNote")}</span>
        </div>
      ))}
      {enterToSendHint}
      {followUpSuggestions}
      {selectedSkillBadges}
      {/* One border-box control height; icon buttons keep their compact width. */}
      <div ref={internalRowRef} className={cn(
        "flex flex-row items-end gap-2 [--composer-control-size:theme(spacing.9)]",
        isMobile && "[--composer-control-size:theme(spacing.11)]",
      )}>
        <PromptInputTextarea
          className={cn(
            "min-w-0 min-h-[var(--composer-control-size)] py-[calc((var(--composer-control-size)-1lh-2px)/2)]",
            textareaClassName,
          )}
          name={textareaName}
          placeholder={placeholder}
          aria-label={inputAriaLabel}
          autoFocusKey={autoFocusKey ?? inputKey}
          onChange={(event) => {
            promptInputValueRef.current = event.currentTarget.value;
            setDraftLength(event.currentTarget.value.length);
            onInputChange?.(event.currentTarget.value);
          }}
          onMultilineChange={handleMultilineChange}
          onKeyDown={onKeyDown}
          submitShortcut={submitShortcut}
          onVoiceDictationShortcut={
            onVoiceDictationShortcut ?? (voiceDictationShortcutTarget ? handleVoiceDictationShortcut : undefined)
          }
        />
        <div className={cn("flex shrink-0 items-end gap-1", promptActionsVertical && "flex-col")}>
          {typeof quickActions === "function" ? quickActions({ stacked: promptActionsVertical }) : quickActions}
          <AttachFilesButton />
          <span className={cn("flex", isMobile && isKeyboardOpen && "hidden")}>
            <VoiceNoteButton
              ref={voiceNoteRef}
              onSubmitVoiceNote={handleVoiceNoteSubmit}
              onRecordingChange={(recording) => { setIsRecordingVoice(recording); onRecordingChange?.(recording); }}
              disabled={controlsDisabled}
            />
          </span>
          <PromptInputSubmit
            ref={voiceSubmitRef}
            // Keep this mounted so PromptInputTextarea can still submit with Ctrl/Cmd+Enter.
            className={cn(hideSubmit && !isRecordingVoice && "hidden")}
            status={status}
            isUploading={isUploading}
            isTranscribing={isTranscribing}
            isRecordingVoice={isRecordingVoice}
            onVoiceSubmit={onVoiceSubmit ?? (() => voiceNoteRef.current?.submitVoiceNote())}
            onStop={onStop}
            disabled={submitDisabled || disabled || undefined}
          />
          {stopControl}
        </div>
      </div>
    </PromptInput>
  );
}
